<!-- Template for the action button that parent component can render -->
<ng-template #actionButton>
  <iris-button
    id="pendo-ebcg-payment-info-submission"
    data-test="button-add-payment-method"
    [type]="IrisButtonTypes.PRIMARY"
    [typeAttribute]="IrisButtonTypeAttributeOptions.BUTTON"
    [iconPosition]="IrisButtonIconPositions.RIGHT"
    [disabled]="!allowSubmit()"
    (click)="openPaymentProvider()"
  >
    Add payment method
    <iris-icon
      [name]="'external_link'" 
      [size]="IrisIconSizes.SM" 
      [color]="allowSubmit() ? IconColors.WHITE : IconColors.BLACK">
    </iris-icon>
  </iris-button>
</ng-template>

@if (!paymentFailed()) {
  <div class="preamble">
    <h1>You're nearly there</h1>
    @if (isUnder18()) {
      <p class="subtitle">
        Please add legal guardian information and your payment method for any co-pays that may be charged by your insurance.
      </p>
    } @else {
      <p class="subtitle">
        Please add your payment method for any co-pays that may be charged by your insurance.
      </p>
    }
  </div>
}

<!-- Error screen - timeout or payment failed -->
@if (paymentFailed()) {
  <iris-card>
    <div class="error-content">
      <h2>Something went wrong</h2>
      <p class="error-text">
        We weren't able to complete your booking but your provider is still available for scheduling.
      </p>
      <p class="error-text">
        Go to your care team page to schedule a session with {{ providerName() }}.
      </p>
      <iris-button
        class="full-width-button"
        [type]="IrisButtonTypes.PRIMARY"
        [typeAttribute]="IrisButtonTypeAttributeOptions.BUTTON"
        (click)="navigateToCareTeamPage()"
      >
        Go to care team
      </iris-button>
    </div>
  </iris-card>
}

<!-- Waiting for payment screen -->
@if (waitingForPayment()) {
  <iris-card>
    <div class="waiting-content">
      <h3>Enter your payment details in new browser tab</h3>
      <p class="waiting-text">
        A secure payment page has opened in a new tab. Once you've added your payment method, 
        this page will automatically update and your session will be scheduled.
      </p>
      <p class="waiting-link-text">
        Didn't see a new page open? 
        <a 
          [href]="octaveDetails()?.ebcgBillingPortalUrl" 
          target="_blank"
          rel="noopener noreferrer"
          class="iris-link-responsive"
        >
          Click here to add your payment method
        </a>
      </p>
    </div>
  </iris-card>
}

<!-- Initial payment form -->
@if (!waitingForPayment() && !paymentFailed()) {
  <form [formGroup]="ebcgAgreementForm">
    @if (isUnder18()) {
      <iris-card>
        <div class="payment-wrapper" formGroupName="guardianInfo">
          <h3>Legal guardian information for {{ clientName() }}</h3>
          
          <div class="guardian-form-row">
            <iris-form-field label="First Name">
              <iris-text-input
                formControlName="guardianFirstName"
                data-test="guardian-first-name"
                required="true"
              ></iris-text-input>
            </iris-form-field>

            <iris-form-field label="Last Name">
              <iris-text-input
                formControlName="guardianLastName"
                data-test="guardian-last-name"
                required="true"
              ></iris-text-input>
            </iris-form-field>
          </div>
          <iris-form-field label="Email address">
            <iris-text-input
              formControlName="guardianEmail"
              data-test="guardian-email"
              type="email"
              required="true"
            ></iris-text-input>
          </iris-form-field>

          <iris-form-field label="Phone number">
            <iris-text-input
              formControlName="guardianPhone"
              data-test="guardian-phone"
              type="tel"
              [mask]="phoneNumberMask.ngxMask"
            ></iris-text-input>
          </iris-form-field>
        </div>
      </iris-card>
    }

    <iris-card>
      <div>
        <div class="payment-wrapper">
          <h3 data-test="ebcg-payment-method-header-text">
            Payment method
          </h3>
          <p>
            For your sessions with {{ providerName() }}, your claims will be handled by 
            Evernorth Behavioral Care Group, SonderMind's trusted partner for Cigna insurance.
          </p>
        </div>

        <div class="payment-wrapper">
          <iris-checkbox 
            formControlName="acceptedEbcgTerms" 
            data-test="ebcg-agreement-checkbox"
          >
            I agree to the 
            <a 
              class="iris-link-responsive" 
              [href]="ebcgTermsOfService" 
              target="_blank"
              rel="noopener noreferrer"
            >Terms of Service</a>
            and 
            <a 
              class="iris-link-responsive" 
              [href]="ebcgPrivacyPolicy" 
              target="_blank"
              rel="noopener noreferrer"
            >Privacy Policy</a>
            from Evernorth Behavioral Care Group
          </iris-checkbox>
        </div>

        @if (!fromDirectBooking() && !paymentFailed()) {
          <div class="full-width-button-container">
            <ng-container *ngTemplateOutlet="actionButton"></ng-container>
          </div>
        }
      </div>
    </iris-card>
  </form>
}
